// src/services/PageWebSocketService.ts
type MessageHandler = (data: any) => void;

class PageWebSocketService {
  private socket: WebSocket | null = null;
  private messageHandlers: Map<string, Set<MessageHandler>> = new Map();
  private currentRoom: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 3000;
  private isConnecting = false;
  private userId: string | null = null;
  private userRole: string | null = null;

  constructor() {
    this.userId = localStorage.getItem("userid");
    this.userRole = localStorage.getItem("userRole")?.toLowerCase() || null;
  }

  // Connect and join a specific room/channel
  async connectToRoom(roomId: string): Promise<void> {
    if (this.currentRoom === roomId && this.isConnected()) {
      return; // Already connected to this room
    }

    // If connected to different room, disconnect first
    if (this.socket && this.isConnected() && this.currentRoom !== roomId) {
      this.disconnect();
    }

    this.currentRoom = roomId;
    await this.connect();
  }

  // Connect to WebSocket
  private connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      if (this.isConnecting) {
        return;
      }

      this.isConnecting = true;
      const WS_ENDPOINT = process.env.REACT_APP_WS_URL!;
      
      try {
        const apiToken = localStorage.getItem("apiToken");
        const wsUrlWithToken = `${WS_ENDPOINT}?authorization=${encodeURIComponent(`Bearer ${apiToken}`)}`;
        this.socket = new WebSocket(wsUrlWithToken);
        this.socket.onopen = () => {
          console.log("Page WebSocket connected");
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          
          // Auto-join the current room
          if (this.currentRoom) {
            console.log("Auto-joining room:", this.currentRoom); // Add this
            this.joinRoom(this.currentRoom);
          }
          
          resolve();
        };

        this.socket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error("Error parsing page WebSocket message:", error);
          }
        };

        this.socket.onclose = () => {
          console.log("Page WebSocket disconnected");
          this.isConnecting = false;
          
          // Only reconnect if we have a current room
          if (this.currentRoom) {
            this.attemptReconnect();
          }
        };

        this.socket.onerror = (error) => {
          console.error("Page WebSocket error:", error);
          this.isConnecting = false;
          reject(error);
        };

      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  // Disconnect WebSocket
  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.currentRoom = null;
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnection
  }

  // Join room using your existing backend
  private joinRoom(roomId: string) {
    if (!this.isConnected()) {
      console.log("Cannot join room - not connected"); // Add this
      return;
    }

    const message = {
      action: "joinRoom",
      roomId: roomId,
      userId: this.userId,
      userRole: this.userRole
    };

    console.log("Joining room with message:", message); // Add this

    this.socket!.send(JSON.stringify(message));
  }

  // Subscribe to messages from current room
  subscribe(handler: MessageHandler) {
    if (!this.currentRoom) return;

    if (!this.messageHandlers.has(this.currentRoom)) {
      this.messageHandlers.set(this.currentRoom, new Set());
    }
    this.messageHandlers.get(this.currentRoom)!.add(handler);
  }

  // Unsubscribe from messages
  unsubscribe(handler: MessageHandler) {
    if (!this.currentRoom) return;

    const handlers = this.messageHandlers.get(this.currentRoom);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  // Send message to current room
  sendMessage(data: any) {
    if (!this.isConnected() || !this.currentRoom) {
      console.warn("Page WebSocket not connected or no room joined. Cannot send message.");
      return;
    }

    const message = {
      action: "sendMessage",
      roomId: this.currentRoom,
      message: data.message || JSON.stringify(data),
      senderRole: this.userRole,
      messageType: data.messageType || "page_update",
      data: data
      // Note: no ticketId for page-level messages
    };

    this.socket!.send(JSON.stringify(message));
  }

  // Handle incoming messages
  private handleMessage(message: any) {
    console.log("Raw message received:", message); // Add this
    const { roomId, type } = message;
    console.log("Message roomId:", roomId, "currentRoom:", this.currentRoom, "type:", type); // Add this

    // Only handle messages for current room
    if (roomId === this.currentRoom && type === "stream-message") {
      console.log("Processing message for handlers"); // Add this
      const handlers = this.messageHandlers.get(roomId);
      if (handlers) {
        console.log("Found handlers:", handlers.size); // Add this
        handlers.forEach(handler => handler(message));
      } else {
        console.log("No handlers found for room:", roomId); // Add this
      }
    }
  }

  // Check connection status
  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  // Get current room
  getCurrentRoom(): string | null {
    return this.currentRoom;
  }

  // Get connection state
  getConnectionState() {
    return {
      isConnected: this.isConnected(),
      isConnecting: this.isConnecting,
      currentRoom: this.currentRoom,
      reconnectAttempts: this.reconnectAttempts
    };
  }

  // Attempt to reconnect
  private attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts || !this.currentRoom) {
      console.log("Max reconnection attempts reached or no room to reconnect to");
      return;
    }

    this.reconnectAttempts++;
    console.log(`Attempting to reconnect to room ${this.currentRoom}... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      this.connect().catch(error => {
        console.error("Reconnection failed:", error);
      });
    }, this.reconnectInterval);
  }
}

// Create singleton instance
export const pageWebSocket = new PageWebSocketService();
export default pageWebSocket;